import { useEffect, useCallback, useMemo } from "react"
import { useStore } from "react-redux"
import { useAccountAlias, AI_MODULE_LIMIT } from "resources/account"
import { useAnalytics } from "connected-components/analytics"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { DASHBOARD_EVENTS as DASHBOARD_ANALYTICS_EVENTS } from "../../analytics/events"
import { useAppSelector } from "hooks/redux"
import type { ReduxState } from "model/state"
import { useDashboardId } from "../../context/dashboard-id"
import { useRegisterMountedContent } from "../../context/mounted-content"
import { useAddModule } from "../use-add-module"
import { useUpdateModule } from "../use-update-module"
import { EMPTY_MODULES } from "../../store/panels-slice"
import { MODULE_TYPES } from "../../modules/types"
import { DEFAULT_ARTIFACT_TITLES_BY_KIND } from "../../modules/ai-artifact/constants"
import type { Artifact, ArtifactKind } from "../../modules/ai-artifact/types"
import { usePinWorkflow } from "connected-components/ai-teammate/hooks/use-pin-workflow"
import { log } from "helpers/logger"
import { getErrorMessage } from "helpers/dev-logger"
import { getPinnedArtifactIds, getUniqueModuleTitle, isSuccessfullyPinnedArtifactModule } from "./helpers"

interface PinRequestPayload {
  artifact: Artifact
  kind: ArtifactKind
  title?: string
}

export function usePinArtifactHandler() {
  const dashboardId = useDashboardId()
  useRegisterMountedContent("pin", dashboardId)
  const store = useStore<ReduxState>()
  const panels = useAppSelector((s) => s.dashboard.panels[dashboardId])
  const modules = panels?.modules ?? EMPTY_MODULES
  const isPreferencesLoaded = panels?.isPreferencesLoaded ?? false
  const analytics = useAnalytics()

  const addModule = useAddModule()
  const updateModule = useUpdateModule()

  const aiModuleCount = useMemo(
    () => modules.filter((module) => module.type === MODULE_TYPES.AI_ARTIFACT).length,
    [modules],
  )
  const pinnedArtifactIds = useMemo(() => getPinnedArtifactIds(modules), [modules])
  const { mutateAsync: pinWorkflow } = usePinWorkflow()
  const accountAlias = useAccountAlias()

  const handlePinRequest = useCallback(async ({ artifact, kind, title }: PinRequestPayload) => {
    // Read directly from the Redux store so we always see the latest state,
    // bypassing React's render cycle (which may lag behind store.dispatch).
    const currentModules = store.getState().dashboard.panels[dashboardId]?.modules ?? []

    const existingModule = currentModules.find(
      (module) =>
        isSuccessfullyPinnedArtifactModule(module)
        && module.config?.artifactId === artifact.id,
    )
    if (existingModule) {
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId: existingModule.id,
        pinnedId: existingModule.config?.pinnedId ?? "",
        kind,
      })
      return
    }

    const currentAiModuleCount = currentModules.filter((m) => m.type === MODULE_TYPES.AI_ARTIFACT).length
    if (currentAiModuleCount >= AI_MODULE_LIMIT) {
      const errorMessage = `AI module limit of ${AI_MODULE_LIMIT} reached`
      log(`Cannot pin artifact: ${errorMessage}`)
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        kind,
      })
      return
    }

    let moduleId: string | undefined

    try {
      const resolvedTitle = getUniqueModuleTitle(
        title || DEFAULT_ARTIFACT_TITLES_BY_KIND[kind],
        currentModules,
      )

      const addConfig = {
        artifactId: artifact.id,
        kind,
        title: resolvedTitle,
        isPinning: true,
      }
      moduleId = addModule({
        type: MODULE_TYPES.AI_ARTIFACT,
        config: addConfig,
      })
      analytics.track(DASHBOARD_ANALYTICS_EVENTS.MODULE_ADDED, {
        moduleType: MODULE_TYPES.AI_ARTIFACT,
        kind: addConfig.kind,
        title: addConfig.title,
        isPinning: addConfig.isPinning,
      })

      const result = await pinWorkflow({
        accountAlias,
        artifactId: artifact.id,
        title: resolvedTitle,
      })

      updateModule(moduleId, {
        config: {
          artifactId: artifact.id,
          kind,
          title: resolvedTitle,
          pinnedId: result.pinnedId,
          isPinning: false,
        },
      })

      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId,
        pinnedId: result.pinnedId,
        kind,
      })
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      log(`Failed to pin artifact: ${errorMessage}`)

      if (moduleId) {
        updateModule(moduleId, {
          config: {
            isPinning: false,
            hasError: true,
          },
        })
      }

      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        moduleId,
        kind,
      })
    }
  }, [store, dashboardId, addModule, updateModule, pinWorkflow, accountAlias, analytics])

  useEffect(() => {
    if (!isPreferencesLoaded) return

    dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_LIMIT_STATUS, {
      count: aiModuleCount,
      isLimitReached: aiModuleCount >= AI_MODULE_LIMIT,
      pinnedArtifactIds,
    })
  }, [isPreferencesLoaded, aiModuleCount, pinnedArtifactIds])

  useEffect(() => {
    return dashboardEvents.on<PinRequestPayload>(
      DASHBOARD_EVENTS.ARTIFACT_PIN_REQUESTED,
      handlePinRequest,
    )
  }, [handlePinRequest])
}
